<script lang="ts" setup>
import { UserStatisticsChartInterval } from '@/types';

const session = useSessionStore();
const { t: $t } = useI18n();

onMounted(async () => {
    await nextTick();
    chartData.value = setChartData();
    chartOptions.value = setChartOptions();
});

const chartData = ref();
const chartOptions = ref();

const setChartData = () => {
    const documentStyle = getComputedStyle(document.body);
    const existing = session.federalStateList.filter((v) => v.exists).length;

    return {
        labels: [$t('ux.labels.setup'), $t('ux.labels.pending')],
        datasets: [
            {
                data: [existing, 16 - existing],
                backgroundColor: [
                    documentStyle.getPropertyValue('--p-cyan-500'),
                    documentStyle.getPropertyValue('--p-gray-500')
                ],
                hoverBackgroundColor: [
                    documentStyle.getPropertyValue('--p-cyan-400'),
                    documentStyle.getPropertyValue('--p-gray-400')
                ]
            }
        ]
    };
};

const setChartOptions = () => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color');
    return {
        plugins: {
            legend: {
                labels: {
                    usePointStyle: false,
                    color: textColor
                },
                position: 'bottom'
            }
        }
    };
};

watch(isDark, () => {
    nextTick(() => {
        chartData.value = setChartData();
        chartOptions.value = setChartOptions();
    });
});
const jobTableType = ref('0');
const userStatisticsType = ref(UserStatisticsChartInterval.Daily);
const userStatisticsTypes = computed(() => [
    { text: $t('ux.labels.daily'), value: UserStatisticsChartInterval.Daily },
    {
        text: $t('ux.labels.cal_week'),
        value: UserStatisticsChartInterval.Weekly
    },
    {
        text: $t('ux.labels.monthly'),
        value: UserStatisticsChartInterval.Monthly
    },
    {
        text: $t('ux.labels.quarterly'),
        value: UserStatisticsChartInterval.Quarterly
    },
    { text: $t('ux.labels.yearly'), value: UserStatisticsChartInterval.Yearly }
]);
const userStatisticsUnique = ref('y');
const userStatisticsUniqueOpts = computed(() => [
    { text: $t('ux.labels.user'), value: 'y' },
    { text: $t('ux.labels.sessions'), value: 'n' }
]);
</script>

<template>
    <div>
        <SimpleCard>
            <SimpleCardTitle>{{ $t('ux.labels.admin') }}</SimpleCardTitle>
            <Divider />
            <div class="flex flex-col items-center justify-center">
                <div class="font-semibold mb-2">
                    {{ $t('ux.labels.states') }}
                </div>
                <Chart
                    type="pie"
                    :data="chartData"
                    :options="chartOptions"
                    class="w-full md:w-48"
                />
            </div>
        </SimpleCard>
        <SimpleCard
            v-if="Number(session.selectedContext.rootId?._id ?? 0) === 0"
            class="my-8"
        >
            <div class="flex justify-between items-center flex-col sm:flex-row">
                <div>
                    <SimpleCardTitle>{{
                        $t('ux.labels.user_statistics')
                    }}</SimpleCardTitle>
                </div>
                <div class="flex">
                    <InputGroup class="sm:w-96! mr-2">
                        <InputGroupAddon>
                            <i class="pi pi-chart-line" />
                        </InputGroupAddon>
                        <Select
                            v-model="userStatisticsUnique"
                            class="flex-1 w-full"
                            option-label="text"
                            option-value="value"
                            :options="userStatisticsUniqueOpts"
                        />
                    </InputGroup>
                    <InputGroup class="sm:w-96!">
                        <InputGroupAddon>
                            <i class="pi pi-clock" />
                        </InputGroupAddon>
                        <Select
                            v-model="userStatisticsType"
                            class="flex-1 w-full"
                            option-label="text"
                            option-value="value"
                            :options="userStatisticsTypes"
                        />
                    </InputGroup>
                </div>
            </div>
            <Divider />
            <ChartLoader :key="`${isDark ? 'dark' : 'light'}-userstats`">
                <UserStatisticsChart
                    :federal-state-id="0"
                    :ty="userStatisticsType"
                    :unique="userStatisticsUnique === 'y'"
                />
            </ChartLoader>
        </SimpleCard>
        <SimpleCard class="my-8">
            <SimpleCardTitle>{{
                $t('ux.labels.automated_jobs')
            }}</SimpleCardTitle>
            <Divider />
            <Tabs v-model:value="jobTableType" class="mb-2">
                <TabList>
                    <Tab value="0">{{ $t('ux.labels.active') }}</Tab>
                    <Tab value="1">{{ $t('ux.labels.errored') }}</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel value="0">
                        <AdminJobTable />
                    </TabPanel>
                    <TabPanel value="1">
                        <AdminJobTable dead />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </SimpleCard>
    </div>
</template>
